import React, { useState, useEffect, useMemo } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle, Lightbulb, X } from 'lucide-react';

import { ClientSearchBox } from '@/components/pages/shared/ClientSearchBox';
import { useBonos } from '@/components/hooks/useBonos';
import { useClientes, Cliente } from '@/components/hooks/useClientes';
import { DLG_BTN_PRIMARY } from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSold: () => void;
}

export const SellBonusDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onSold,
}) => {
  const empresaId = localStorage.getItem('id_empresa') || '';
  const usuarioId = localStorage.getItem('id_usuario') || '';

  const {
    bonos,
    loading: loadingBonos,
    refetch: refetchBonos,
    comprarBono,
  } = useBonos(empresaId, { tipo: 'catalogo' });

  const [idBono, setIdBono] = useState('');
  const [loading, setLoading] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any | null>(
    null
  );
  const [clienteTexto, setClienteTexto] = useState('');

  const { getClientesEmpresa } = useClientes();
  const [clientesEmpresa, setClientesEmpresa] = useState<Cliente[]>([]);
  const idEmpresa = localStorage.getItem('id_empresa');

  useEffect(() => {
    if (!idEmpresa) return;
    getClientesEmpresa(idEmpresa).then(setClientesEmpresa);
  }, [idEmpresa, getClientesEmpresa]);

  useEffect(() => {
    if (open) {
      setIdBono('');
      setClienteSeleccionado(null);
      setClienteTexto('');
      refetchBonos();
    }
  }, [open]);

  const bonoSeleccionado = useMemo(
    () => bonos.find((b) => b.id_bono === idBono),
    [bonos, idBono]
  );

  const handleSell = async () => {
    if (!empresaId || !usuarioId) {
      toast.error('Sesión inválida');
      return;
    }
    if (!idBono) {
      toast.error('Selecciona un bono');
      return;
    }
    if (!clienteSeleccionado) {
      toast.error('Selecciona un cliente');
      return;
    }

    setLoading(true);

    const { ok, error } = await comprarBono({
      id_bono: idBono,
      id_usuario: clienteSeleccionado.id_usuario,
      usuario_creacion: usuarioId,
    });

    setLoading(false);

    if (!ok) {
      console.error(error);
      toast.error('Error al vender bono');
      return;
    }

    toast.success('Bono vendido correctamente');
    onSold();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button[type='button']]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              Vender bono
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5 space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 text-yellow-800 dark:text-yellow-200 text-sm shadow-sm flex gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <div>
              Para vender un bono, el cliente debe estar registrado en Bookoll.
              <div className="flex gap-2 mt-1">
                <Lightbulb className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                <span>
                  Si aún no existe, añádelo como <strong>cliente</strong> desde
                  tu empresa.
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-5 text-sm text-gray-700 dark:text-gray-300">
            {/* Seleccionar bono */}
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Bono *</Label>
              <Select
                value={idBono}
                onValueChange={setIdBono}
                disabled={loadingBonos}
              >
                <SelectTrigger className="w-full mt-1 h-9 text-sm focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                  <SelectValue placeholder="Seleccionar bono" />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {bonos.map((b) => (
                    <SelectItem
                      key={b.id_bono}
                      value={b.id_bono}
                      className="text-sm"
                    >
                      {b.nombre_bono} — {b.nombre_servicio} ({b.precio_total} €)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {bonoSeleccionado && (
                <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <p>
                    Usos permitidos:{' '}
                    <strong>{bonoSeleccionado.cantidad_servicios}</strong>
                  </p>
                  <p>
                    Duración:{' '}
                    <strong>{bonoSeleccionado.duracion_dias} días</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Seleccionar cliente */}
            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Cliente *
                <span className="text-gray-500 dark:text-gray-400">
                  {' '}
                  (teléfono, email o nombre)
                </span>
              </Label>

              <div className="mt-1">
                <ClientSearchBox
                  clientes={clientesEmpresa}
                  value={clienteTexto}
                  onChange={(v) => {
                    setClienteTexto(v);
                    if (!v) setClienteSeleccionado(null);
                  }}
                  onSelect={(c) => {
                    setClienteSeleccionado(c);
                    setClienteTexto(c.nombre || c.telefono || c.email || '');
                  }}
                  placeholder="Buscar cliente..."
                />
              </div>

              {clienteSeleccionado && (
                <p className="mt-2 text-xs text-green-600 dark:text-green-400">
                  Cliente seleccionado:{' '}
                  <strong>{clienteSeleccionado.nombre}</strong>
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            onClick={handleSell}
            disabled={loading || !idBono || !clienteSeleccionado}
            className={`w-full ${DLG_BTN_PRIMARY}`}
          >
            {loading ? 'Vendiendo…' : 'Vender bono'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};